import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, InputNumber, Select, message } from 'antd';
import { PackageMinus, Plus, Trash2 } from 'lucide-react';
import Layout from '../../Layout';
import { khoApi, useKho } from './khoStore';
import { avgCost, fmtMoney, fmtNum, itemById, normCost, serviceUsage, type NormLine } from './khoLogic';
import { PageHeader, Panel, newDocUrl, useKhoPerms } from './khoUi';

const D = 86400000;

/** Định mức vật tư cho từng dịch vụ: chi phí / buổi, so với thực tế 30 ngày */
export default function NormsPage() {
  const s = useKho();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { canEdit, canCreate } = useKhoPerms();
  const serviceId = params.get('service') || s.services[0]?.id;
  const service = s.services.find(x => x.id === serviceId);
  const [lines, setLines] = useState<NormLine[]>([]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { setLines(s.norms[serviceId] || []); setDirty(false); }, [serviceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const usage = useMemo(() => serviceUsage(s, Date.now() - 30 * D, Date.now()), [s]);
  const cost = lines.reduce((t, l) => t + l.qty * avgCost(s, l.itemId), 0);
  const u = usage.find(x => x.service.id === serviceId);
  const edit = (i: number, p: Partial<NormLine>) => { setLines(ls => ls.map((l, j) => (j === i ? { ...l, ...p } : l))); setDirty(true); };

  const pick = (id: string) => {
    if (dirty && !window.confirm('Định mức đang sửa chưa lưu. Bỏ thay đổi?')) return;
    setParams({ service: id }, { replace: true });
  };

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Định mức vật tư dịch vụ" desc="Mỗi buổi dịch vụ dùng bao nhiêu thuốc, vật tư — dùng để tự điền phiếu xuất, tính giá vốn dịch vụ và phát hiện dùng vượt" />

        <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4 items-start">
          <Panel title={`Dịch vụ (${s.services.length})`}>
            <div className="p-2 flex flex-col">
              {s.services.map(sv => {
                const c = normCost(s, sv.id);
                const pct = sv.price ? Math.round((c / sv.price) * 100) : 0;
                const us = usage.find(x => x.service.id === sv.id);
                const over = us && us.normCost ? Math.round(((us.actualCost - us.normCost) / us.normCost) * 100) : 0;
                return (
                  <button key={sv.id} type="button" onClick={() => pick(sv.id)}
                    className={`text-left px-3 py-2 rounded-lg transition ${sv.id === serviceId ? 'bg-[#3e91ab]/10 ring-1 ring-[#3e91ab]' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
                    <div className="flex justify-between gap-2">
                      <span className="font-medium text-sm text-gray-900 dark:text-gray-100">{sv.name}</span>
                      {over > 10 && <span className="text-[11px] text-rose-600 font-semibold whitespace-nowrap">vượt {over}%</span>}
                    </div>
                    <div className="text-[11px] text-gray-500">
                      {(s.norms[sv.id] || []).length ? `${(s.norms[sv.id] || []).length} vật tư · ${fmtMoney(c)}/buổi · ${pct}% giá DV` : <span className="text-amber-600">Chưa có định mức</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          </Panel>

          {service && (
            <div className="flex flex-col gap-4 min-w-0">
              <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
                <Stat label="Giá dịch vụ" value={fmtMoney(service.price)} />
                <Stat label="Chi phí vật tư / buổi" value={fmtMoney(cost)} hint={service.price ? `${Math.round((cost / service.price) * 100)}% giá dịch vụ` : undefined} />
                <Stat label="Số buổi 30 ngày" value={u ? `${u.sessions}` : '0'} />
                <Stat label="Thực tế / định mức (30 ngày)" value={u && u.normCost ? `${Math.round((u.actualCost / u.normCost) * 100)}%` : '—'}
                  tone={u && u.normCost && u.actualCost > u.normCost * 1.1 ? 'text-rose-600' : 'text-emerald-600'}
                  hint={u ? `${fmtMoney(u.actualCost)} so với ${fmtMoney(u.normCost)}` : 'Chưa có buổi nào'} />
              </div>

              <Panel title={`Định mức — ${service.name}`}
                extra={<div className="flex gap-2">
                  {canCreate && <Button size="small" icon={<PackageMinus size={14} />} onClick={() => navigate(newDocUrl('issue_service', `service=${service.id}`))}>Xuất kho cho 1 buổi</Button>}
                  {canEdit && <Button size="small" type="primary" disabled={!dirty} onClick={() => { khoApi.saveNorm(service.id, lines); setDirty(false); message.success('Đã lưu định mức'); }}>Lưu định mức</Button>}
                </div>}>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[620px]">
                    <thead className="text-xs text-gray-500 bg-gray-50 dark:bg-gray-800/60">
                      <tr>
                        <th className="text-left font-medium px-3 py-2">Vật tư</th>
                        <th className="text-right font-medium px-3 py-2 w-[170px]">Số lượng / buổi</th>
                        <th className="text-right font-medium px-3 py-2 w-[140px]">Giá vốn BQ</th>
                        <th className="text-right font-medium px-3 py-2 w-[140px]">Chi phí / buổi</th>
                        <th className="text-right font-medium px-3 py-2 w-[150px]">Thực tế TB / buổi</th>
                        <th className="w-10" />
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((l, i) => {
                        const item = itemById(s, l.itemId);
                        const act = u?.items.find(x => x.itemId === l.itemId);
                        const avg = act && u!.sessions ? act.actual / u!.sessions : null;
                        return (
                          <tr key={i} className="border-t border-gray-100 dark:border-gray-800">
                            <td className="px-3 py-2">
                              <Select className="w-full" showSearch optionFilterProp="label" disabled={!canEdit} value={l.itemId || undefined} placeholder="Chọn vật tư"
                                onChange={v => edit(i, { itemId: v })}
                                options={s.items.filter(x => x.active && x.kind !== 'cosmetic' && x.kind !== 'tool').map(x => ({ value: x.id, label: `${x.code} · ${x.name}`, disabled: lines.some((y, j) => j !== i && y.itemId === x.id) }))} />
                            </td>
                            <td className="px-3 py-2 text-right">
                              <InputNumber className="!w-[100px]" min={0} disabled={!canEdit} value={l.qty} onChange={v => edit(i, { qty: v || 0 })} />
                              <span className="text-xs text-gray-500 ml-1">{item?.unit}</span>
                            </td>
                            <td className="px-3 py-2 text-right text-gray-500">{item ? fmtMoney(avgCost(s, item.id)) : '—'}</td>
                            <td className="px-3 py-2 text-right font-medium">{item ? fmtMoney(l.qty * avgCost(s, item.id)) : '—'}</td>
                            <td className={`px-3 py-2 text-right ${avg !== null && avg > l.qty * 1.1 ? 'text-rose-600 font-semibold' : 'text-gray-500'}`}>{avg !== null ? `${fmtNum(avg)} ${item?.unit}` : '—'}</td>
                            <td className="px-2 text-center">
                              {canEdit && <button type="button" aria-label="Xóa vật tư" className="text-gray-400 hover:text-rose-600 p-1" onClick={() => { setLines(ls => ls.filter((_, j) => j !== i)); setDirty(true); }}><Trash2 size={15} /></button>}
                            </td>
                          </tr>
                        );
                      })}
                      {!lines.length && <tr><td colSpan={6} className="text-center text-gray-400 py-6">Chưa có vật tư nào trong định mức</td></tr>}
                    </tbody>
                  </table>
                </div>
                {canEdit && (
                  <div className="px-3 py-3 border-t border-gray-100 dark:border-gray-800">
                    <Button size="small" icon={<Plus size={14} />} onClick={() => { setLines(ls => [...ls, { itemId: '', qty: 1 }]); setDirty(true); }}>Thêm vật tư</Button>
                  </div>
                )}
              </Panel>

              {u && u.byPractitioner.length > 0 && (
                <Panel title="Theo người thực hiện (30 ngày)">
                  <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                    {u.byPractitioner.map(p => {
                      const pct = p.normCost ? Math.round(((p.actualCost - p.normCost) / p.normCost) * 100) : 0;
                      return (
                        <div key={p.name} className="border border-gray-100 dark:border-gray-800 rounded-lg px-3 py-2">
                          <div className="font-medium text-sm">{p.name}</div>
                          <div className="text-xs text-gray-500">{p.sessions} buổi · {fmtMoney(p.actualCost / p.sessions)}/buổi</div>
                          <div className={`text-xs font-semibold ${pct > 10 ? 'text-rose-600' : pct > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>{pct > 0 ? `Vượt định mức ${pct}%` : 'Đúng định mức'}</div>
                        </div>
                      );
                    })}
                  </div>
                </Panel>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

const Stat = ({ label, value, hint, tone = '' }: { label: string; value: string; hint?: string; tone?: string }) => (
  <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl px-4 py-3">
    <div className="text-xs text-gray-500">{label}</div>
    <div className={`text-lg font-bold ${tone || 'text-gray-900 dark:text-gray-100'}`}>{value}</div>
    {hint && <div className="text-[11px] text-gray-400">{hint}</div>}
  </div>
);
